import {ObjUtil} from 'yougile-util'
import {
  BoardPermissions,
  ColumnPermissions,
  DataObjectId,
  getDefaultProjectPermissions,
  PermissionCustomRole,
  PermissionObject,
  PermissionRoleKey,
  ProjectPermissions,
  SandboxTree,
  TaskPermissions,
  User
} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {DepartmentsClient} from '@client/departments-client'
import {EmployeeSettings} from '@client/employee-settings'
import {Sync} from '@client/sync'
import {Sort} from './types'

export const sortWithFunc =
  (fn: (u: User) => string, asc: boolean) =>
  (a: User, b: User): number => {
    const fa = fn(a)
    const fb = fn(b)
    if (!fa && !fb) {
      return 0
    }
    if (!fa) {
      return 1
    }
    if (!fb) {
      return -1
    }
    return (asc ? 1 : -1) * (fa > fb ? 1 : fa === fb ? 0 : -1)
  }

export const compare = (
  sortType: Sort,
  asc: boolean,
  chosenRoles: {[id: string]: PermissionRoleKey},
  box: SandboxTree
): ((a: User, b: User) => number) => {
  if (sortType === 'name') {
    return sortWithFunc(u => Accounts.getName(u.id).toLowerCase(), asc)
  } else if (sortType === 'tick') {
    return sortWithFunc(u => (+!!chosenRoles[u.id] ? 'a' : 'b'), asc)
  } else if (sortType === 'roles') {
    return sortWithFunc(u => roleKeyToSortString(chosenRoles[u.id]), asc)
  } else if (sortType === 'deps') {
    return sortWithFunc(
      u =>
        DepartmentsClient.listForUser(box, u.id)
          .map(d => d.title)
          .join(', '),
      asc
    )
  } else if (sortType === 'position') {
    return sortWithFunc(u => EmployeeSettings.getPosition(u.id), asc)
  } else {
    return sortWithFunc(u => u.id, asc)
  }
}

const roleKeyToSortString = (role: PermissionRoleKey): string => {
  if (role === 'admin') {
    return 'a'
  } else if (role === 'observer') {
    return 'b'
  } else if (role === 'worker') {
    return 'c'
  } else if (role) {
    return 'd'
  } else {
    return ''
  }
}

export const arePermissionsChangedForObject = (
  role: PermissionCustomRole,
  objectId: DataObjectId,
  type: PermissionObject['type']
): boolean => {
  if (type === 'project') {
    const defaultPermissions = getDefaultProjectPermissions()
    const permissions = {...role.permissions}
    delete permissions.children
    return !ObjUtil.deepEqual(permissions, defaultPermissions, true)
  } else {
    const childPermissions = role.permissions.children?.[objectId]
    if (childPermissions?.type === type) {
      const permissionsFromParent = getPermissionsFromRoleForObjectFromParent(
        role,
        objectId,
        type
      )
      return !ObjUtil.deepEqual(
        childPermissions.permissions,
        permissionsFromParent.permissions,
        true
      )
    }
  }
  return false
}

const getPermissionsFromRoleForProject = (
  role: PermissionCustomRole
): ProjectPermissions => role.permissions

const getPermissionsFromRoleForBoardFromParent = (
  role: PermissionCustomRole
): BoardPermissions => getPermissionsFromRoleForProject(role).boards

const getPermissionsFromRoleForBoard = (
  role: PermissionCustomRole,
  boardId: DataObjectId | undefined
): BoardPermissions => {
  const childPermissions = boardId && role.permissions.children?.[boardId]
  if (childPermissions?.type === 'board') {
    return childPermissions?.permissions
  }
  return getPermissionsFromRoleForBoardFromParent(role)
}

const getPermissionsFromRoleForColumnFromParent = (
  role: PermissionCustomRole,
  columnId: DataObjectId | undefined
): ColumnPermissions => {
  const column = Sync.getCurrentSandbox().getHub(columnId)
  return getPermissionsFromRoleForBoard(role, column?.location).columns
}

const getPermissionsFromRoleForColumn = (
  role: PermissionCustomRole,
  columnId: DataObjectId | undefined
): ColumnPermissions => {
  const childPermissions = columnId && role.permissions.children?.[columnId]
  if (childPermissions?.type === 'column') {
    return childPermissions?.permissions
  }
  return getPermissionsFromRoleForColumnFromParent(role, columnId)
}

const getPermissionsFromRoleForTaskFromParent = (
  role: PermissionCustomRole,
  taskId: DataObjectId | undefined
): TaskPermissions => {
  const task = Sync.getCurrentSandbox().getHub(taskId)
  return getPermissionsFromRoleForColumn(role, task?.location).allTasks
}

const getPermissionsFromRoleForTask = (
  role: PermissionCustomRole,
  taskId: DataObjectId | undefined
): TaskPermissions => {
  const childPermissions = taskId && role.permissions.children?.[taskId]
  if (childPermissions?.type === 'task') {
    return childPermissions?.permissions
  }
  return getPermissionsFromRoleForTaskFromParent(role, taskId)
}

export const getPermissionsFromRoleForObject = (
  role: PermissionCustomRole,
  objectId: DataObjectId,
  type: PermissionObject['type']
): PermissionObject => {
  if (type === 'project') {
    const permissions = getPermissionsFromRoleForProject(role)
    return {type, objectId, permissions}
  } else if (type === 'board') {
    const permissions = getPermissionsFromRoleForBoard(role, objectId)
    return {type, objectId, permissions}
  } else if (type === 'column') {
    const permissions = getPermissionsFromRoleForColumn(role, objectId)
    return {type, objectId, permissions}
  } else if (type === 'task') {
    const permissions = getPermissionsFromRoleForTask(role, objectId)
    return {type, objectId, permissions}
  }
  throw new Error(`Unknown type ${type}`)
}

export const getPermissionsFromRoleForObjectFromParent = (
  role: PermissionCustomRole,
  objectId: DataObjectId,
  type: PermissionObject['type']
): PermissionObject => {
  if (type === 'project') {
    const permissions = getPermissionsFromRoleForProject(role)
    return {type, objectId, permissions}
  } else if (type === 'board') {
    const permissions = getPermissionsFromRoleForBoardFromParent(role)
    return {type, objectId, permissions}
  } else if (type === 'column') {
    const permissions = getPermissionsFromRoleForColumnFromParent(
      role,
      objectId
    )
    return {type, objectId, permissions}
  } else if (type === 'task') {
    const permissions = getPermissionsFromRoleForTaskFromParent(role, objectId)
    return {type, objectId, permissions}
  }
  throw new Error(`Unknown type ${type}`)
}
